import { useId } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { smoothEase } from '@/lib/motion'

/**
 * Editorial hero artwork — moss primary washes, brush stroke, and filigree.
 * Colors read from theme tokens so they track palette updates.
 */
export function HomeHeroArtwork() {
  const reduced = useReducedMotion()
  const brushId = useId().replace(/:/g, '')

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {/* 1. Soft primary / sage radial washes */}
      <div
        className="absolute inset-0"
        style={{
          background: [
            'radial-gradient(ellipse 70% 55% at 82% 18%, color-mix(in srgb, var(--color-primary) 28%, transparent), transparent 60%)',
            'radial-gradient(ellipse 55% 45% at 8% 92%, color-mix(in srgb, var(--color-sage-wash) 75%, transparent), transparent 65%)',
          ].join(', '),
        }}
      />

      {/* 2. Morphing watercolor blob */}
      <motion.svg
        className="absolute right-[-10%] top-[-12%] h-[62vmin] max-h-[560px] w-[62vmin] max-w-[560px]"
        viewBox="0 0 400 400"
        fill="none"
        style={{ filter: 'blur(32px)' }}
      >
        <motion.path
          style={{ fill: 'color-mix(in srgb, var(--color-primary) 24%, transparent)' }}
          initial={{
            d: 'M210,60 C300,70 360,140 350,230 C340,320 240,360 160,330 C80,300 50,210 80,140 C110,70 160,50 210,60 Z',
          }}
          animate={
            reduced
              ? undefined
              : {
                  d: [
                    'M210,60 C300,70 360,140 350,230 C340,320 240,360 160,330 C80,300 50,210 80,140 C110,70 160,50 210,60 Z',
                    'M220,50 C320,80 350,160 330,240 C310,320 220,370 140,340 C70,310 60,200 90,130 C120,60 170,40 220,50 Z',
                    'M200,70 C290,60 370,150 360,240 C350,330 230,350 150,320 C90,290 40,200 90,140 C140,80 150,80 200,70 Z',
                    'M210,60 C300,70 360,140 350,230 C340,320 240,360 160,330 C80,300 50,210 80,140 C110,70 160,50 210,60 Z',
                  ],
                }
          }
          transition={{ duration: 24, repeat: Infinity, ease: 'easeInOut' }}
        />
      </motion.svg>

      {/* 3. Painterly brush stroke */}
      <svg
        className="absolute left-[-6%] top-[8%] hidden h-[110%] w-[min(58vw,560px)] sm:block"
        viewBox="0 0 600 800"
        fill="none"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <linearGradient id={`hero-brush-${brushId}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--color-primary-active)" stopOpacity="0.75" />
            <stop offset="55%" stopColor="var(--color-primary)" stopOpacity="0.55" />
            <stop offset="100%" stopColor="var(--color-sage-wash)" stopOpacity="0" />
          </linearGradient>
          <filter id={`hero-brush-grain-${brushId}`} x="-5%" y="-5%" width="110%" height="110%">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="3" />
            <feDisplacementMap in="SourceGraphic" scale="3" />
          </filter>
        </defs>
        <motion.path
          d="M -40 740 C 80 600, 180 540, 220 420 C 260 290, 180 220, 240 120 C 290 40, 380 40, 460 -20"
          stroke={`url(#hero-brush-${brushId})`}
          strokeWidth="58"
          strokeLinecap="round"
          fill="none"
          opacity={0.2}
          filter={`url(#hero-brush-grain-${brushId})`}
          initial={{ pathLength: 0, opacity: 0 }}
          animate={
            reduced
              ? { pathLength: 1, opacity: 0.2 }
              : { pathLength: 1, opacity: [0, 0.24, 0.18, 0.22, 0.18] }
          }
          transition={
            reduced
              ? { duration: 0 }
              : {
                  pathLength: { duration: 2.4, ease: smoothEase },
                  opacity: { duration: 9, repeat: Infinity, ease: 'easeInOut', delay: 2.4 },
                }
          }
        />
      </svg>

      {/* 4. Primary filigree curves */}
      <svg
        className="absolute -bottom-2 right-[-4%] hidden w-[min(46vw,420px)] text-primary md:block"
        viewBox="0 0 460 260"
        fill="none"
      >
        <motion.path
          d="M 8 220 C 80 180, 130 200, 180 160 C 230 120, 260 70, 320 70 C 380 70, 410 120, 444 152"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
          fill="none"
          className="opacity-60"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.6 }}
          transition={{
            pathLength: { duration: reduced ? 0 : 2.8, ease: smoothEase, delay: reduced ? 0 : 0.4 },
            opacity: { duration: reduced ? 0 : 0.6, delay: reduced ? 0 : 0.4 },
          }}
        />
        <motion.path
          d="M 60 200 C 110 188, 150 196, 196 174"
          stroke="currentColor"
          strokeWidth="0.85"
          strokeLinecap="round"
          fill="none"
          className="opacity-40"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: reduced ? 0 : 2.4, ease: smoothEase, delay: reduced ? 0 : 1.4 }}
        />
        <motion.circle
          cx="444"
          cy="152"
          r="3"
          fill="currentColor"
          className="opacity-70"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.7 }}
          transition={{ duration: 0.4, delay: reduced ? 0 : 3.0, ease: smoothEase }}
        />
      </svg>
    </div>
  )
}
